import { Button } from "@mui/material";
import { isEmpty } from "lodash-es";
import { useCallback } from "react";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router";
import { useApiServiceCallback } from "../../../../server/api/client";
import { useDialog } from "../../../../utils/hooks/use-dialog";
import { useAnalytics } from "../../../analytics/analytics";
import { MomentService } from "../../../moment/client";
import { useHandleSave } from "../../../moment/component/buttons/hooks/useHandleSave";
import { FollowUpPublishDialog } from "./follow-up-publish-dialog";

import type { HandleSaveMoment } from "../../../moment/component/buttons/hooks/types";
import type { FollowUpButtonsProps } from "./types";
import type { FC } from "react";

export const FollowUpPublishButton: FC<FollowUpButtonsProps & { disabled: boolean }> = ({
  saveData,
  errors,
  usesRestrictedData,
  uiData,
  moment,
  saving,
  onSaving,
  onSaved,
  onPublished,
  showErrors,
  disabled,
  dynamicOnlyLinks,
  dynamicOnlySurveyIds,
}) => {
  const navigate = useNavigate();
  const dialog = useDialog<HandleSaveMoment>();

  const { callback: save } = useHandleSave({
    reset: () => {},
    validate: () => Promise.resolve(isEmpty(errors.errors)),
  });

  const { gaEvent } = useAnalytics();

  const { handleOpen } = dialog;

  const handleCloseDialog = useCallback(() => {
    dialog.handleClose();
    if (saveData.follow_up?.id) {
      void navigate(`/follow-up/${saveData.follow_up?.id}`, { replace: true });
    }
  }, [dialog, navigate, saveData]);

  const handleSubmit = useApiServiceCallback(
    MomentService,
    async ({ apiService }): Promise<void> => {
      onSaving();

      const { error } = await apiService.setActiveStatus(saveData.id, true, true);
      if (error) {
        toast.error(error);
        onSaved(error);
        gaEvent("publish_error", { event_category: "follow_up", moment_id: saveData.id });
      } else {
        toast.success("Follow-Up published!", { duration: 5000 });
        gaEvent("publish", { event_category: "follow_up", moment_id: saveData.id });
        handleCloseDialog();
        onPublished();
      }
    },
    [gaEvent, handleCloseDialog],
  );

  const handleSave = useCallback(() => {
    if (errors.errors) {
      showErrors();
      toast.error("Please fix the errors before publishing.");
      return;
    }

    if (usesRestrictedData) {
      toast.error(
        "The selected segment or email address is restricted. Please choose a different one before publishing.",
      );
      return;
    }

    onSaving();

    void save(saveData)
      .then(() => {
        onSaved(null);
        handleOpen();
      })
      .catch((error: Error) => {
        onSaved(error.message);
      });
  }, [errors, saveData, save, onSaving, onSaved, showErrors, handleOpen, usesRestrictedData]);

  if (!uiData.reason || !uiData.channel) {
    return null;
  }

  return (
    <>
      <Button
        data-analytics-id="follow-up-publish-button"
        sx={{ ml: 2 }}
        loading={saving}
        disabled={disabled}
        onClick={handleSave}
        id="publishButton"
        variant="contained"
        color={errors.display && errors.errors !== null ? "error" : "primary"}
      >
        Publish Follow-Up
      </Button>
      <FollowUpPublishDialog
        reason={uiData.reason}
        channel={uiData.channel}
        schedule={uiData.schedule}
        moment={moment}
        open={dialog.open}
        onCancel={handleCloseDialog}
        onConfirm={() => {
          void handleSubmit().catch(console.error);
        }}
        saving={saving}
        dynamicOnlyLinks={dynamicOnlyLinks ?? new Set()}
        dynamicOnlySurveyIds={dynamicOnlySurveyIds ?? new Set()}
      />
    </>
  );
};
